import { AudioOutlined, CaretRightOutlined, DeleteOutlined, LoadingOutlined, PauseOutlined } from '@ant-design/icons';
import { Alert, Flex, Progress, Space, Typography } from 'antd';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';
import { ArtifactMediaFieldState } from 'components/Artifacts/editorTypes';
import { ArtifactMedia } from 'components/Artifacts/types';
import { usePermissionsService } from 'components/Message/usePermissionsService';
import { useReactMediaRecorder } from 'components/Tool/ReactMediaRecorder/ReactMediaRecorder';
import {
  ArtifactMediaValidationErrorCode,
  getArtifactMediaConstraint,
  validateArtifactMediaFile
} from 'constants/artifactMediaConstraints';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { sessionActivity } from 'utils/sessionActivity';
import { bytesToSize } from 'utils/utils';

import 'components/Artifacts/ArtifactsExperience.css';

import { useArtifactMediaUpload } from './useArtifactMediaUpload';

const { Paragraph, Text } = Typography;

type ArtifactAudioFieldProps = {
  initialMedia?: ArtifactMedia | null;
  onChange: (state: ArtifactMediaFieldState) => void;
};

export type ArtifactAudioFieldHandle = {
  finalizeRecordingForSave: () => Promise<ArtifactMedia | null>;
};

const getErrorMessage = (errorCode: ArtifactMediaValidationErrorCode, maxFileSize: number) => {
  if (errorCode === 'file_too_large') {
    return `Audio size should be less than ${bytesToSize(maxFileSize)}.`;
  }

  return 'Unsupported audio type.';
};

const sectionBadgeStyle = {
  alignItems: 'center',
  background: 'linear-gradient(180deg, #7c6cff 0%, #5f4bf4 100%)',
  borderRadius: '999px',
  color: '#fff',
  display: 'inline-flex',
  fontSize: 12,
  fontWeight: 700,
  height: 20,
  justifyContent: 'center',
  minWidth: 20
} as const;

const formatRecordingTime = (seconds: number) => {
  const finiteSeconds = Number.isFinite(seconds) ? seconds : 0;
  const safeSeconds = Math.max(0, Math.floor(finiteSeconds));
  const minutes = Math.floor(safeSeconds / 60)
    .toString()
    .padStart(2, '0');
  const remainingSeconds = (safeSeconds % 60).toString().padStart(2, '0');

  return `${minutes}:${remainingSeconds}`;
};

export const ArtifactAudioField = forwardRef<ArtifactAudioFieldHandle, ArtifactAudioFieldProps>(function ArtifactAudioField(
  { initialMedia = null, onChange },
  ref
) {
  const breakpoint = useBreakpoint();
  const discardNextRecordingRef = useRef(false);
  const recordingStartedAtRef = useRef<number | null>(null);
  const recordingElapsedSecondsRef = useRef(0);
  const finalizeRecordingPromiseRef = useRef<Promise<ArtifactMedia | null> | null>(null);
  const finalizeRecordingResolveRef = useRef<((media: ArtifactMedia | null) => void) | null>(null);
  const finalizeRecordingRejectRef = useRef<((error: Error) => void) | null>(null);
  const mediaConstraint = getArtifactMediaConstraint('artifactAudio');
  const [recordingElapsedSeconds, setRecordingElapsedSeconds] = useState(0);
  const { state, uploadFile, reset, setError } = useArtifactMediaUpload({
    mediaKind: 'AUDIO',
    initialMediaId: initialMedia?.id,
    initialMediaUrl: initialMedia?.url || null
  });

  const clearPendingRecordingFinalization = useCallback(() => {
    finalizeRecordingPromiseRef.current = null;
    finalizeRecordingResolveRef.current = null;
    finalizeRecordingRejectRef.current = null;
  }, []);

  const ensurePendingRecordingFinalization = useCallback(() => {
    if (!finalizeRecordingPromiseRef.current) {
      finalizeRecordingPromiseRef.current = new Promise<ArtifactMedia | null>((resolve, reject) => {
        finalizeRecordingResolveRef.current = resolve;
        finalizeRecordingRejectRef.current = reject;
      });
    }

    return finalizeRecordingPromiseRef.current;
  }, []);

  const resolvePendingRecordingFinalization = useCallback(
    (media: ArtifactMedia | null) => {
      finalizeRecordingResolveRef.current?.(media);
      clearPendingRecordingFinalization();
    },
    [clearPendingRecordingFinalization]
  );

  const rejectPendingRecordingFinalization = useCallback(
    (error: unknown) => {
      const resolvedError = error instanceof Error ? error : new Error('We could not save the recording. Please try again.');
      finalizeRecordingRejectRef.current?.(resolvedError);
      clearPendingRecordingFinalization();
    },
    [clearPendingRecordingFinalization]
  );

  const handleSelectedFile = useCallback(
    async (file?: File | null) => {
      if (!file) {
        return null;
      }

      const validationResult = await validateArtifactMediaFile(file, 'artifactAudio');
      if (!validationResult.isValid) {
        const message = getErrorMessage(validationResult.errorCode, mediaConstraint.maxFileSize);
        setError(message);
        throw new Error(message);
      }

      const uploadedMedia = await uploadFile(file);

      if (!uploadedMedia) {
        throw new Error('We could not save the recording. Please try again.');
      }

      return uploadedMedia;
    },
    [mediaConstraint.maxFileSize, setError, uploadFile]
  );

  const {
    status,
    error: recorderError,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearBlobUrl
  } = useReactMediaRecorder({
    video: false,
    audio: true,
    askPermissionOnMount: false,
    mediaRecorderOptions: { mimeType: 'audio/wav' },
    onStop: (_, blob) => {
      if (discardNextRecordingRef.current) {
        discardNextRecordingRef.current = false;
        resolvePendingRecordingFinalization(state.media);
        return;
      }

      const file = new File([blob], `${new Date().getTime()}.wav`, { type: 'audio/wav' });
      void handleSelectedFile(file)
        .then((uploadedMedia) => {
          resolvePendingRecordingFinalization(uploadedMedia);
        })
        .catch((error) => {
          rejectPendingRecordingFinalization(error);
        });
    }
  });

  const { permissionState, requestPermissions } = usePermissionsService({
    onSuccess: startRecording
  });

  useEffect(() => {
    onChange(state);
  }, [onChange, state]);

  useEffect(
    () => () => {
      rejectPendingRecordingFinalization(new Error('The recording was interrupted before it could be saved.'));
    },
    [rejectPendingRecordingFinalization]
  );

  useEffect(() => {
    const isRecordingActive = status === 'recording' || status === 'paused';
    sessionActivity.setRecordingActive(isRecordingActive);

    return () => {
      sessionActivity.setRecordingActive(false);
    };
  }, [status]);

  useEffect(() => {
    if (status === 'recording') {
      if (!recordingStartedAtRef.current) {
        recordingStartedAtRef.current = Date.now() - recordingElapsedSecondsRef.current * 1000;
      }

      const intervalId = window.setInterval(() => {
        const base = recordingStartedAtRef.current || Date.now();
        const nextRecordingElapsedSeconds = Math.floor((Date.now() - base) / 1000);
        recordingElapsedSecondsRef.current = nextRecordingElapsedSeconds;
        setRecordingElapsedSeconds(nextRecordingElapsedSeconds);
      }, 250);

      return () => window.clearInterval(intervalId);
    }

    if (status === 'paused') {
      recordingStartedAtRef.current = null;
    }

    if (status === 'idle') {
      recordingStartedAtRef.current = null;
      recordingElapsedSecondsRef.current = 0;
      setRecordingElapsedSeconds(0);
    }

    return undefined;
  }, [status]);

  const handleStartRecording = async () => {
    clearBlobUrl();
    discardNextRecordingRef.current = false;
    recordingElapsedSecondsRef.current = 0;
    setRecordingElapsedSeconds(0);
    recordingStartedAtRef.current = null;
    await requestPermissions({ audio: true });
  };

  const handleDiscardRecording = () => {
    clearBlobUrl();
    recordingStartedAtRef.current = null;
    recordingElapsedSecondsRef.current = 0;
    setRecordingElapsedSeconds(0);

    if (status === 'recording' || status === 'paused') {
      discardNextRecordingRef.current = true;
      stopRecording();
      return;
    }

    reset();
  };

  const hasRecordedAudio = Boolean(state.previewUrl || state.media?.url);
  const isRecordingPaused = status === 'paused';
  const isRecording = status === 'recording';
  const isRecordingInProgress = isRecording || isRecordingPaused;
  const isRecorderBusy = status === 'acquiring_media';
  const isCompactControls = !breakpoint.sm;
  const recordButtonStateClassName = isRecording ? 'is-pause' : isRecordingPaused ? 'is-resume' : 'is-record';
  const recordButtonLabel = isRecording ? 'Pause' : isRecordingPaused ? 'Resume' : 'Record';

  const finalizeRecordingForSave = useCallback(async () => {
    if (status === 'recording' || status === 'paused') {
      const pendingFinalization = ensurePendingRecordingFinalization();
      stopRecording();
      return pendingFinalization;
    }

    if (status === 'stopping') {
      return ensurePendingRecordingFinalization();
    }

    return state.media;
  }, [ensurePendingRecordingFinalization, state.media, status, stopRecording]);

  useImperativeHandle(
    ref,
    () => ({
      finalizeRecordingForSave
    }),
    [finalizeRecordingForSave]
  );

  return (
    <div
      style={{
        background: '#fff',
        border: '1px solid #f1f2f8',
        borderRadius: 16,
        padding: 16
      }}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Flex align="center" gap={8} wrap="wrap">
          <span style={sectionBadgeStyle}>4</span>
          <Text style={{ color: '#26233a', fontSize: 15, fontWeight: 600 }}>Audio Story</Text>
          <Text style={{ color: '#9aa0b4', fontSize: 13, fontWeight: 500 }}>(optional)</Text>
        </Flex>

        <Paragraph type="secondary" style={{ marginBottom: 0, marginTop: -4 }}>
          Record your story or upload an audio file.
        </Paragraph>

        {(state.errorMessage || recorderError || permissionState === 'denied') && (
          <Alert
            type="error"
            showIcon
            message={
              state.errorMessage ||
              recorderError ||
              'Microphone access was denied. You can still upload an audio file instead.'
            }
          />
        )}

        <div className="artifact-audio-field__recorder">
          <button
            type="button"
            className={`artifact-audio-field__mic ${isRecordingInProgress ? 'is-active' : ''}`}
            onClick={() => {
              if (isRecordingInProgress) {
                stopRecording();
                return;
              }

              void handleStartRecording();
            }}
            disabled={isRecorderBusy}
            aria-label={isRecordingInProgress ? 'Stop recording (microphone button)' : 'Start recording'}
          >
            <AudioOutlined />
          </button>

          <p className="artifact-audio-field__timer">{formatRecordingTime(recordingElapsedSeconds)}</p>
          <p className="artifact-audio-field__subtext">Recording will appear here</p>

          <div
            className={`artifact-audio-field__controls ${isRecordingInProgress ? 'is-active' : hasRecordedAudio ? 'is-stopped' : ''} ${isCompactControls ? 'is-compact' : ''}`}
          >
            <button
              type="button"
              className={`artifact-audio-field__control-button is-primary ${recordButtonStateClassName}`}
              onClick={() => {
                if (isRecording) {
                  pauseRecording();
                  return;
                }

                if (isRecordingPaused) {
                  resumeRecording();
                  return;
                }

                void handleStartRecording();
              }}
              disabled={isRecorderBusy}
              aria-label={isRecording ? 'Pause recording' : isRecordingPaused ? 'Resume recording' : 'Record'}
              title={recordButtonLabel}
            >
              {isRecording ? (
                <PauseOutlined />
              ) : isRecordingPaused ? (
                <CaretRightOutlined />
              ) : (
                <span className="artifact-audio-field__record-dot" aria-hidden="true" />
              )}
              {!isCompactControls ? (
                <span className="artifact-audio-field__control-label">{recordButtonLabel}</span>
              ) : null}
            </button>

            <button
              type="button"
              className="artifact-audio-field__control-button is-secondary"
              onClick={() => stopRecording()}
              disabled={!isRecordingInProgress}
              aria-label="Stop recording"
              title="Stop"
            >
              <span className="artifact-audio-field__stop-icon" aria-hidden="true" />
              {!isCompactControls ? <span className="artifact-audio-field__control-label">Stop</span> : null}
            </button>

            <button
              type="button"
              className="artifact-audio-field__control-button is-discard"
              onClick={handleDiscardRecording}
              disabled={!isRecordingInProgress && !hasRecordedAudio}
              aria-label="Discard recording"
              title="Discard"
            >
              <DeleteOutlined />
              {!isCompactControls ? <span className="artifact-audio-field__control-label">Discard</span> : null}
            </button>
          </div>

          {hasRecordedAudio && (
            <audio controls style={{ marginTop: 8, width: '100%' }} src={state.previewUrl || state.media?.url || undefined} />
          )}
        </div>

        {(state.phase === 'uploading' || state.phase === 'processing') && (
          <div>
            <Text>{state.phase === 'uploading' ? 'Uploading audio' : 'Processing audio'}</Text>
            <Progress percent={state.progressPercent} size="small" />
          </div>
        )}

        {(state.phase === 'uploading' || state.phase === 'processing') && (
          <LoadingOutlined style={{ color: '#6d5efc' }} />
        )}
      </Space>
    </div>
  );
});

export default ArtifactAudioField;
